import type { RecordingId } from "@repo/app-types/entities/recording";
import {
  buildRecordingTimelineEvents,
  type AdminApiRecordingToolActivity,
  type FragmentTimelineEvent,
  type RecordingTimelineEvent,
  type ToolActivityTimelineEvent,
} from "@repo/app-types/entities/recordingToolActivity";
import type {
  AccountGradingRecording,
  CustomerInteractionTextThread,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { FloatingAutoScrollResumeButton } from "@repo/ui/transcript/autoScrollResumeButton";
import debounce from "lodash-es/debounce.js";
import { DateTime } from "luxon";
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { twMerge } from "tailwind-merge";
import {
  AUDIO_URL_REFRESH_INTERVAL_MS,
  useGenerateAudioUrlsQuery,
} from "../../api/entities/recording.js";
import { useAllToolsQuery } from "../../api/entities/tool.js";
import type { AudioPlayerSource } from "../../components/audioPlayer/context.js";
import { useAudioPlayer } from "../../components/audioPlayer/hook.js";
import { AudioPlayerPlayButton } from "../../components/audioPlayer/playButton.js";
import { PlayableTranscriptMessage } from "../../components/transcript/playableTranscriptMessage.js";
import { TranscriptCallMarker } from "../../components/transcript/transcriptCallMarker.js";
import { TranscriptContainer } from "../../components/transcript/transcriptContainer.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import type { CallPosition } from "./callNavigationHelpers.js";
import { renderCallDuration } from "./durationFormatting.js";
import { QuoteMatchMap } from "./gradeValidationHelpers.js";
import { renderSpeakerName } from "./gradingHelpers.js";
import { buildGradingTranscriptItems } from "./gradingTranscriptItems.js";
import { GradingTranscriptTextStretch } from "./gradingTranscriptTextStretch.js";
import { ToolActivityBlock } from "./gradingTranscriptToolActivityBlock.js";
import { RecordingFragmentText } from "./snippetSharing/recordingFragmentText.js";
import { SnippetSharePopover } from "./snippetSharing/snippetSharePopover.js";
import { useTranscriptQuoteFromUrl } from "./snippetSharing/useTranscriptQuoteFromUrl.js";
import { useTranscriptSnippetSharing } from "./snippetSharing/useTranscriptSnippetSharing.js";
import { useTranscriptAudioSync } from "./useTranscriptAudioSync.js";

//
// Props.
//

interface FocusQuoteArgs {
  container: HTMLElement;
  quoteId: string;
  highlightQuote: boolean;
}

export interface GradingTranscriptWithIndexHandle {
  focusQuote: (args: FocusQuoteArgs) => void;
  /**
   * Focuses verbatim quoted text, fuzzy-matched against the recordings. What a
   * citation in the chat has to work with: it carries the words, not an id in
   * any match map.
   */
  focusQuoteText: (args: {
    quote: string;
    recordingId?: string | undefined;
  }) => void;
}

interface GradingTranscriptWithIndexProps {
  initialScrollTop?: number;
  recordings: readonly AccountGradingRecording[];
  /** The interaction's texts, shown between its calls in the order they happened. */
  textThread?: CustomerInteractionTextThread | undefined;
  quoteMatches?: QuoteMatchMap;
  searchMatches?: QuoteMatchMap;
  scrollToCallPosition?: (position: CallPosition) => void;
  toolActivitiesByRecordingId:
    | Record<string, readonly AdminApiRecordingToolActivity[]>
    | undefined;
}

//
// Style.
//

const blue100 = "#dbeafe";
const blue300 = "#93c5fd";
const amber100 = "#fef3c7";
const amber300 = "#fcd34d";

//
// Component.
//

export const GradingTranscriptWithIndex = memo(
  forwardRef<GradingTranscriptWithIndexHandle, GradingTranscriptWithIndexProps>(
    (props, ref) => {
      const {
        recordings,
        textThread,
        initialScrollTop,
        quoteMatches,
        searchMatches,
        scrollToCallPosition,
        toolActivitiesByRecordingId,
      } = props;
      const containerRef = useRef<HTMLDivElement>(null);
      // Retain indexed texts for quotes and search, but do not render them as calls.
      const callRecordings = useMemo(
        () =>
          recordings.filter(
            (recording) => !textThread?.recording_ids.includes(recording.id),
          ),
        [recordings, textThread],
      );

      const { data: audioUrls } = useGenerateAudioUrlsQuery(
        callRecordings.map((recording) => recording.id),
        { pollingInterval: AUDIO_URL_REFRESH_INTERVAL_MS },
      );

      // Fetch all tools for looking up tool settings (e.g., destination for transfers)
      const { data: allTools } = useAllToolsQuery();

      // Build timeline events per recording by combining fragments and tool activities
      const timelineEventsPerRecording = useMemo(() => {
        const result: Record<string, RecordingTimelineEvent[]> = {};

        for (const recording of callRecordings) {
          if (!recording.recorded_at || !recording.transcript) {
            continue;
          }

          const recordingStartTimeMs = new Date(
            recording.recorded_at,
          ).getTime();
          const toolActivities =
            toolActivitiesByRecordingId?.[recording.id] ?? [];

          result[recording.id] = buildRecordingTimelineEvents(
            recording.transcript.fragments,
            toolActivities,
            recordingStartTimeMs,
          );
        }

        return result;
      }, [callRecordings, toolActivitiesByRecordingId]);

      const transcriptItems = useMemo(
        () =>
          buildGradingTranscriptItems(callRecordings, textThread?.texts ?? []),
        [callRecordings, textThread],
      );

      const getAudioUrl = useCallback(
        (recordingId: RecordingId) => {
          return audioUrls?.find((url) => url.id === recordingId)?.audio_url;
        },
        [audioUrls],
      );

      const audioPlayerSources = useMemo(
        () =>
          callRecordings.map((recording, index): AudioPlayerSource => ({
            key: recording.id,
            title: `Call ${index + 1} (${renderCallDuration(recording.duration)})`,
            sortTimestamp: DateTime.fromISO(recording.recorded_at).toMillis(),
            fragmentTimes: recording.transcript?.fragments.map(
              (fragment) => fragment.start_time / 1000,
            ),
            src: getAudioUrl(recording.id),
          })),
        [callRecordings, getAudioUrl],
      );

      const { currentSourceKey, nativeAudio, seek, hasRequestedPosition } =
        useAudioPlayer({
          sources: audioPlayerSources,
        });
      const seekTranscriptPosition = useCallback(
        (recordingId: string, time: number) => {
          if (
            callRecordings.some((recording) => recording.id === recordingId)
          ) {
            seek(recordingId, time);
          } else {
            nativeAudio?.pause();
          }
        },
        [callRecordings, nativeAudio, seek],
      );

      const activeRecording = callRecordings.find(
        (recording) => recording.id === currentSourceKey,
      );
      const { isAutoScrollSuspended, resumeAutoScroll, resumeDirection } =
        useTranscriptAudioSync(
          containerRef,
          nativeAudio,
          activeRecording,
          hasRequestedPosition,
          initialScrollTop,
        );

      const { activeSnippet: activeSnippetFromSnippetSharing, snippetToShare } =
        useTranscriptSnippetSharing({
          containerRef,
          recordings,
          seek: seekTranscriptPosition,
        });

      const {
        activeSnippet: activeSnippetFromTranscriptQuote,
        focusQuoteText,
      } = useTranscriptQuoteFromUrl({
        containerRef,
        recordings,
        seek: seekTranscriptPosition,
        hasRequestedPosition,
        scrollToCallPosition,
      });

      useImperativeHandle(ref, () => ({
        focusQuoteText,
        async focusQuote({ container, quoteId, highlightQuote }) {
          // Scroll to the quote timestamp. Copilot citations live in
          // `quoteMatches`, search targets in `searchMatches`.
          if (!scrollToCallPosition) {
            return;
          }

          const matches =
            (quoteMatches &&
              QuoteMatchMap.findByQuote(quoteMatches, quoteId)) ||
            (searchMatches &&
              QuoteMatchMap.findByQuote(searchMatches, quoteId));
          if (!matches || matches.length === 0) {
            return;
          }

          // Get the first match
          const firstMatch = matches[0];
          if (!firstMatch) {
            return;
          }
          const recording = recordings.find(
            (r) => r.id === firstMatch.recordingId,
          );
          if (!recording?.transcript) {
            return;
          }

          // Get the fragment timestamp
          const fragment =
            recording.transcript.fragments[firstMatch.recordingFragmentIndex];
          if (!fragment) {
            return;
          }

          // Scroll to the fragment's timestamp
          seekTranscriptPosition(
            firstMatch.recordingId,
            fragment.start_time / 1000,
          );

          scrollToCallPosition({
            recordingId: firstMatch.recordingId,
            timestamp: fragment.start_time,
          });

          if (!highlightQuote) {
            return;
          }

          // Flash the quote — amber for search anchors, blue for citations.
          const [base, peak] = quoteId.startsWith("search-")
            ? [amber100, amber300]
            : [blue100, blue300];
          await waitForScrollEnd(container);
          const quoteElement = document.getElementById(`quote-${quoteId}-0`);
          if (quoteElement) {
            quoteElement.animate(
              [
                { backgroundColor: base, offset: 0 },
                { backgroundColor: peak, offset: 0.25 },
                { backgroundColor: base, offset: 0.5 },
                { backgroundColor: peak, offset: 0.75 },
                { backgroundColor: base, offset: 1 },
              ],
              { duration: 600 },
            );
          }
        },
      }));

      return (
        <div
          ref={containerRef}
          className={twMerge("flex flex-col", PH_MASK_CLASS)}
        >
          <div className="flex flex-col gap-4">
            {transcriptItems.map((item) => {
              if (item.type === "texts") {
                return (
                  <GradingTranscriptTextStretch
                    key={`texts-${item.texts[0]?.id}`}
                    texts={item.texts}
                    speakers={textThread?.speakers ?? []}
                    activeSnippet={
                      activeSnippetFromSnippetSharing ||
                      activeSnippetFromTranscriptQuote
                    }
                    quoteMatches={quoteMatches}
                    searchMatches={searchMatches}
                  />
                );
              }

              const { recording } = item;
              const { recorded_at, duration, transcript } = recording;
              if (!recorded_at || !duration || !transcript) {
                return null;
              }

              const events = timelineEventsPerRecording[recording.id] ?? [];
              const groupedEvents = groupTimelineEvents(events);

              return (
                <TranscriptContainer key={recording.id}>
                  <TranscriptCallMarker
                    type="started"
                    formattedTime={DateTime.fromISO(recorded_at).toLocaleString(
                      DateTime.DATETIME_FULL_WITH_SECONDS,
                    )}
                    action={<AudioPlayerPlayButton sourceKey={recording.id} />}
                  />

                  {groupedEvents.map((groupedEvent, groupIndex) => {
                    if (groupedEvent.type === "fragment_group") {
                      const firstFragment = groupedEvent.fragments[0];
                      if (!firstFragment) {
                        return null;
                      }
                      const fragmentTime = firstFragment.data.start_time / 1000;

                      // The last message stops being active at its own end.
                      const nextFragmentGroup = groupedEvents
                        .slice(groupIndex + 1)
                        .find(isFragmentGroup);
                      const nextFirstFragment = nextFragmentGroup?.fragments[0];
                      const activeUntilTime = nextFirstFragment
                        ? nextFirstFragment.data.start_time / 1000
                        : Math.max(
                            ...groupedEvent.fragments.map(
                              (event) => event.data.end_time,
                            ),
                          ) / 1000;

                      return (
                        <PlayableTranscriptMessage
                          key={`fragment-group-${firstFragment.index}`}
                          sourceKey={recording.id}
                          time={fragmentTime}
                          nextTime={activeUntilTime}
                          speakerName={renderSpeakerName(
                            recording.speakers,
                            firstFragment.data.speaker_id,
                          )}
                          speakerRole={
                            recording.speakers?.find(
                              (speaker) =>
                                speaker.id === firstFragment.data.speaker_id,
                            )?.role_type ?? undefined
                          }
                        >
                          {groupedEvent.fragments.map((fragEvent) => (
                            <div
                              key={`fragment-${fragEvent.index}`}
                              data-fragment-index={fragEvent.index}
                              data-recording-id={recording.id}
                              data-timestamp={fragEvent.data.start_time}
                            >
                              <RecordingFragmentText
                                text={fragEvent.data.text_transcript}
                                recordingId={recording.id}
                                fragmentIndex={fragEvent.index}
                                activeSnippet={
                                  activeSnippetFromSnippetSharing ||
                                  activeSnippetFromTranscriptQuote
                                }
                                quoteMatches={quoteMatches}
                                searchMatches={searchMatches}
                              />
                            </div>
                          ))}
                        </PlayableTranscriptMessage>
                      );
                    }

                    if (groupedEvent.type === "tool_activity") {
                      return (
                        <ToolActivityBlock
                          key={groupedEvent.data.id}
                          activity={groupedEvent.data}
                          tools={allTools}
                        />
                      );
                    }

                    return null;
                  })}

                  <TranscriptCallMarker
                    type="ended"
                    formattedTime={DateTime.fromISO(recorded_at)
                      .plus({ seconds: duration })
                      .toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
                  />
                </TranscriptContainer>
              );
            })}

            {snippetToShare ? (
              <SnippetSharePopover snippetToShare={snippetToShare} />
            ) : null}
          </div>

          <FloatingAutoScrollResumeButton
            isVisible={isAutoScrollSuspended}
            direction={resumeDirection}
            onResume={resumeAutoScroll}
          />
        </div>
      );
    },
  ),
);

/**
 * A grouped timeline event is either a group of consecutive fragments from the
 * same speaker at the same second-level timestamp, or a tool activity event.
 */
type FragmentGroupEvent = {
  type: "fragment_group";
  fragments: readonly FragmentTimelineEvent[];
};
type GroupedTimelineEvent = FragmentGroupEvent | ToolActivityTimelineEvent;

function isFragmentGroup(e: GroupedTimelineEvent): e is FragmentGroupEvent {
  return e.type === "fragment_group";
}

/**
 * Groups consecutive fragment events that share the same speaker and the same
 * second-level timestamp (i.e. same whole second). Tool activity events and
 * speaker/timestamp changes break a group.
 */
function groupTimelineEvents(
  events: readonly RecordingTimelineEvent[],
): GroupedTimelineEvent[] {
  type MutableFragmentGroup = {
    type: "fragment_group";
    fragments: FragmentTimelineEvent[];
  };
  const grouped: (MutableFragmentGroup | ToolActivityTimelineEvent)[] = [];

  for (const event of events) {
    if (event.type === "tool_activity") {
      grouped.push(event);
      continue;
    }

    const lastGroup = grouped.at(-1);
    if (lastGroup?.type === "fragment_group") {
      const lastFragment = lastGroup.fragments.at(-1)!;
      if (
        lastFragment.data.speaker_id === event.data.speaker_id &&
        Math.floor(lastFragment.data.start_time / 1000) ===
          Math.floor(event.data.start_time / 1000)
      ) {
        lastGroup.fragments.push(event);
        continue;
      }
    }

    grouped.push({ type: "fragment_group", fragments: [event] });
  }

  return grouped;
}

function waitForScrollEnd(container: HTMLElement) {
  return new Promise<void>((resolve) => {
    function done() {
      container.removeEventListener("scroll", debouncedDone);
      resolve();
    }

    const debouncedDone = debounce(done, 100);
    container.addEventListener("scroll", debouncedDone, { passive: true });
    debouncedDone();
  });
}
